import { P2GBox, P2GSwitch } from '@parcel2go/core-ui';
import { makeStyles } from 'tss-react/mui';
import { QuoteFilterSectionTitle } from './quote-filter-section';
import { useTaxName } from '@parcel2go/npm-p2g-app';

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    '& .MuiFormControlLabel-labelPlacementStart': {
      marginLeft: '0',
      marginRight: '0'
    }
  },
  vatToggle: {
    width: '79px',
    height: '32px',
    left: '0',
    marginLeft: '0',
    padding: 0,
    pointerEvents: 'auto',
    '& .MuiSwitch-switchBase': {
      '& input:focus-visible + span': { backgroundColor: theme.palette.grey[100] },
      padding: 0,
      transform: 'translate(2px, 2px)',
      '& :after': {
        content: '"EXC"',
        fontSize: '14px',
        right: '-35px',
        top: '3px',
        position: 'absolute'
      },
      '&.Mui-checked': {
        color: theme.palette.background.default,
        transform: 'translate(49px, 2px)',
        '& :after': {
          content: '"INC"',
          fontSize: '14px',
          left: '-30px',
          top: '3px',
          position: 'absolute'
        },
        '& .MuiSwitch-thumb:before': {
          backgroundImage: `url('data:image/svg+xml;utf8,<svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.81294 1.86204C8.86871 1.80893 8.91295 1.74589 8.94314 1.6765C8.97332 1.60711 8.98886 1.53275 8.98886 1.45764C8.98886 1.38254 8.97332 1.30817 8.94314 1.23879C8.91295 1.1694 8.86871 1.10635 8.81294 1.05325C8.75717 1.00014 8.69096 0.958016 8.61809 0.929276C8.54522 0.900535 8.46712 0.885742 8.38825 0.885742C8.30938 0.885742 8.23128 0.900535 8.15841 0.929276C8.08554 0.958016 8.01933 1.00014 7.96356 1.05325L3.40012 5.50003L2.02527 4.18974C1.9695 4.13663 1.90329 4.09451 1.83042 4.06577C1.75755 4.03703 1.67945 4.02223 1.60058 4.02223C1.5217 4.02223 1.4436 4.03703 1.37073 4.06577C1.29786 4.09451 1.23165 4.13663 1.17588 4.18974C1.12011 4.24285 1.07587 4.30589 1.04569 4.37528C1.0155 4.44466 0.999969 4.51903 0.999969 4.59414C0.999969 4.66924 1.0155 4.74361 1.04569 4.81299C1.07587 4.88238 1.12011 4.94543 1.17588 4.99853L2.97543 6.71207C3.03115 6.76526 3.09734 6.80747 3.17022 6.83626C3.24309 6.86506 3.32122 6.87988 3.40012 6.87988C3.47902 6.87988 3.55715 6.86506 3.63002 6.83626C3.7029 6.80747 3.76909 6.76526 3.82481 6.71207L8.81294 1.86204Z" fill="${encodeURIComponent(
            theme.palette.secondary.main
          )}" stroke="${encodeURIComponent(theme.palette.primary.main)}"/></svg>')`
        },
        '& + .MuiSwitch-track': {
          opacity: 1,
          backgroundColor: theme.palette.primary.main
        }
      }
    },
    '& .MuiSwitch-thumb': {
      backgroundColor: 'white',
      width: '28px',
      height: '28px',
      '&:before': {
        content: "''",
        position: 'absolute',
        width: '100%',
        height: '100%',
        left: '0px',
        top: '1px',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundImage: `url('data:image/svg+xml;utf8,<svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.39043 7.80476C6.78095 8.19529 7.41412 8.19529 7.80464 7.80476C8.19517 7.41424 8.19517 6.78107 7.80464 6.39055L5.46289 4.04879L7.80464 1.70704C8.19517 1.31652 8.19517 0.683351 7.80464 0.292827C7.41412 -0.0976983 6.78095 -0.0976983 6.39043 0.292827L4.04867 2.63458L1.70709 0.292997C1.31656 -0.0975271 0.683399 -0.0975272 0.292875 0.292997C-0.0976489 0.683522 -0.0976499 1.31669 0.292875 1.70721L2.63446 4.04879L0.292875 6.39038C-0.0976485 6.7809 -0.0976486 7.41407 0.292876 7.80459C0.6834 8.19512 1.31656 8.19512 1.70709 7.80459L4.04867 5.46301L6.39043 7.80476Z" fill="${encodeURIComponent(
          theme.palette.grey['500']
        )}"/></svg>')`
      }
    },
    '& .MuiSwitch-track': {
      opacity: 1,
      backgroundColor: theme.palette.grey['500'],
      borderRadius: '16px'
    }
  }
}));

type props = {
  onChange: () => void;
  selected: boolean;
};

export const VatToggle = ({ onChange, selected }: props) => {
  const { classes } = useStyles();
  const taxName = useTaxName();
  return (
    <P2GBox classNames={classes.container}>
      <P2GSwitch
        label={''}
        onChange={onChange}
        checked={selected}
        labelPlacement="start"
        dataTestId="vat-toggle-switch"
        data-testid="vat-toggle-switch"
        classNames={classes.vatToggle}
      />
      <QuoteFilterSectionTitle title={`${taxName} Inc/Exc`} />
    </P2GBox>
  );
};
